import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/store/authStore';
import {
  getMeetingsList,
  saveMeetingRow,
  deleteMeetingRow,
  getActivitiesList,
  type MeetingRow,
  type MeetingType,
  type ActivityRow,
} from '@/services/db/dsrEntry';
import { FieldLabel, FieldError, ChipGroup, timeHHMMSS } from './_shared';

const MEETING_TYPES: { value: MeetingType; label: string; color: string }[] = [
  { value: 'toolbox_talk', label: 'Toolbox Talk', color: '#00b4b4' },
  { value: 'pre_job_safety_meeting', label: 'Pre-Job Safety Meeting', color: '#1668a0' },
  { value: 'jsa_review', label: 'JSA Review', color: '#3ba870' },
  { value: 'incident_review', label: 'Incident Review', color: '#d87820' },
  { value: 'emergency_drill_briefing', label: 'Emergency Drill Briefing', color: '#ef4444' },
  { value: 'shift_change_briefing', label: 'Shift Change Briefing', color: '#6b7280' },
  { value: 'other', label: 'Other', color: '#9ca3af' },
];

const entrySchema = z.object({
  meeting_type: z.string().min(1, 'Meeting type is required'),
  topic: z.string().min(1, 'Topic is required').max(300),
  meeting_time: z.string().min(1, 'Meeting time is required'),
  duration_minutes: z.string().optional(),
  attendee_count: z
    .string()
    .min(1, 'Attendee count is required')
    .regex(/^\d+$/, 'Must be a whole number'),
  conducted_by: z.string().min(1, 'Conducted by is required'),
  key_points: z.string().max(500).optional(),
  activity_entry_id: z.string().optional(),
  activity_description: z.string().max(300).optional(),
  comments: z.string().max(300).optional(),
});

type EntryFormValues = z.infer<typeof entrySchema>;

function nowHHMM(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const EMPTY: EntryFormValues = {
  meeting_type: 'toolbox_talk',
  topic: '',
  meeting_time: nowHHMM(),
  duration_minutes: '',
  attendee_count: '',
  conducted_by: '',
  key_points: '',
  activity_entry_id: '',
  activity_description: '',
  comments: '',
};

interface Props {
  dsrId: string;
  isReadOnly?: boolean;
}

export default function SafetyMeetingsForm({ dsrId, isReadOnly }: Props) {
  const tenantId = useAuthStore((s) => s.tenantId);
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'closed' | 'adding' | { editing: MeetingRow }>('closed');

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['entry_meetings', dsrId],
    queryFn: () => getMeetingsList(dsrId),
    staleTime: 30 * 1000,
  });

  const { data: activities = [] } = useQuery({
    queryKey: ['entry_activities', dsrId],
    queryFn: () => getActivitiesList(dsrId),
    staleTime: 30 * 1000,
  });

  const totalMeetings = entries.length;
  const totalAttendees = entries.reduce((sum, m) => sum + m.attendee_count, 0);

  const onSaved = () => {
    setMode('closed');
    queryClient.invalidateQueries({ queryKey: ['entry_meetings', dsrId] });
    queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
  };

  const onDelete = async (id: string) => {
    if (!confirm('Remove this meeting?')) return;
    try {
      await deleteMeetingRow(id);
      queryClient.invalidateQueries({ queryKey: ['entry_meetings', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not delete meeting');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-mozaic-teal" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Safety Meetings</h2>
        <p className="text-xs text-muted-foreground">
          Toolbox talks, pre-job briefings, and other safety meetings held this shift.
        </p>
      </div>

      {entries.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 gap-4">
              <Stat value={totalMeetings} label="Meetings" />
              <Stat value={totalAttendees} label="Total Attendees" />
            </div>
          </CardContent>
        </Card>
      )}

      {entries.length === 0 && mode === 'closed' && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No meetings yet. Add a meeting below.
          </CardContent>
        </Card>
      )}

      {entries.map((entry) => (
        <MeetingCard
          key={entry.id}
          entry={entry}
          onEdit={() => setMode({ editing: entry })}
          onDelete={() => onDelete(entry.id)}
          isReadOnly={isReadOnly}
        />
      ))}

      {!isReadOnly && mode !== 'closed' && tenantId && (
        <MeetingEntryForm
          dsrId={dsrId}
          tenantId={tenantId}
          activities={activities}
          editing={mode !== 'adding' ? mode.editing : undefined}
          onSaved={onSaved}
          onCancel={() => setMode('closed')}
        />
      )}

      {!isReadOnly && mode === 'closed' && (
        <Button
          variant="outline"
          className="w-full border-dashed border-mozaic-teal text-mozaic-teal"
          onClick={() => setMode('adding')}
        >
          <Plus className="mr-1 h-4 w-4" /> Add Meeting
        </Button>
      )}
    </div>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="text-center">
      <div className="text-2xl font-bold text-mozaic-teal">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function MeetingCard({
  entry,
  onEdit,
  onDelete,
  isReadOnly,
}: {
  entry: MeetingRow;
  onEdit: () => void;
  onDelete: () => void;
  isReadOnly?: boolean;
}) {
  const meta = MEETING_TYPES.find((t) => t.value === entry.meeting_type);
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-2 flex items-start justify-between gap-3">
          {meta && (
            <Badge style={{ backgroundColor: meta.color }} className="text-white">
              {meta.label.toUpperCase()}
            </Badge>
          )}
          <span className="text-xs text-muted-foreground">{timeHHMMSS(entry.meeting_time)}</span>
        </div>
        <h3 className="font-semibold text-mozaic-navy">{entry.topic}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {entry.attendee_count} attendee{entry.attendee_count === 1 ? '' : 's'}
          {entry.duration_minutes ? ` · ${entry.duration_minutes} min` : ''}
          {entry.conducted_by ? ` · ${entry.conducted_by}` : ''}
        </p>
        {!isReadOnly && (
          <div className="mt-3 flex gap-2 border-t pt-3">
            <Button variant="ghost" size="sm" className="text-mozaic-teal" onClick={onEdit}>
              <Pencil className="mr-1 h-3 w-3" /> Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={onDelete}
            >
              <Trash2 className="mr-1 h-3 w-3" /> Delete
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MeetingEntryForm({
  dsrId,
  tenantId,
  activities,
  editing,
  onSaved,
  onCancel,
}: {
  dsrId: string;
  tenantId: string;
  activities: ActivityRow[];
  editing?: MeetingRow;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const hasActivities = activities.length > 0;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EntryFormValues>({
    resolver: zodResolver(entrySchema),
    defaultValues: editing
      ? {
          meeting_type: editing.meeting_type,
          topic: editing.topic,
          meeting_time: timeHHMMSS(editing.meeting_time),
          duration_minutes: editing.duration_minutes?.toString() ?? '',
          attendee_count: String(editing.attendee_count),
          conducted_by: editing.conducted_by,
          key_points: editing.key_points ?? '',
          activity_entry_id: editing.activity_entry_id ?? '',
          activity_description: editing.activity_description ?? '',
          comments: editing.comments ?? '',
        }
      : EMPTY,
  });

  const onSubmit = handleSubmit(async (values) => {
    setSaving(true);
    try {
      await saveMeetingRow({
        id: editing?.id,
        dsrReportId: dsrId,
        tenantId,
        meetingType: values.meeting_type as MeetingType,
        topic: values.topic,
        meetingTime: values.meeting_time,
        durationMinutes: values.duration_minutes ? parseInt(values.duration_minutes, 10) : null,
        attendeeCount: parseInt(values.attendee_count, 10),
        conductedBy: values.conducted_by,
        keyPoints: values.key_points || null,
        activityEntryId:
          hasActivities && values.activity_entry_id ? values.activity_entry_id : null,
        activityDescription:
          !hasActivities && values.activity_description ? values.activity_description : null,
        comments: values.comments || null,
      });
      onSaved();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not save meeting');
    } finally {
      setSaving(false);
    }
  });

  return (
    <Card className="border-2 border-mozaic-teal">
      <CardContent className="space-y-4 pt-6">
        <h3 className="font-semibold">{editing ? 'Edit Meeting' : 'New Meeting'}</h3>

        <div className="space-y-2">
          <FieldLabel required>Meeting Type</FieldLabel>
          <Controller
            control={control}
            name="meeting_type"
            render={({ field }) => (
              <ChipGroup
                options={MEETING_TYPES}
                value={field.value as MeetingType}
                onChange={(v) => field.onChange(v)}
                wrap
              />
            )}
          />
          <FieldError message={errors.meeting_type?.message} />
        </div>

        <div className="space-y-2">
          <FieldLabel required>Meeting Topic</FieldLabel>
          <Controller
            control={control}
            name="topic"
            render={({ field }) => (
              <Input {...field} maxLength={300} placeholder="e.g. Lifting operations review" />
            )}
          />
          <FieldError message={errors.topic?.message} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <FieldLabel required>Meeting Time</FieldLabel>
            <Controller
              control={control}
              name="meeting_time"
              render={({ field }) => <Input {...field} type="time" />}
            />
            <FieldError message={errors.meeting_time?.message} />
          </div>
          <div className="space-y-2">
            <FieldLabel>Duration (min)</FieldLabel>
            <Controller
              control={control}
              name="duration_minutes"
              render={({ field }) => (
                <Input {...field} value={field.value ?? ''} type="number" inputMode="numeric" />
              )}
            />
          </div>
          <div className="space-y-2">
            <FieldLabel required>Attendees</FieldLabel>
            <Controller
              control={control}
              name="attendee_count"
              render={({ field }) => (
                <Input {...field} type="number" inputMode="numeric" min={0} placeholder="12" />
              )}
            />
            <FieldError message={errors.attendee_count?.message} />
          </div>
        </div>

        <div className="space-y-2">
          <FieldLabel required>Conducted By</FieldLabel>
          <Controller
            control={control}
            name="conducted_by"
            render={({ field }) => <Input {...field} placeholder="Name of meeting leader" />}
          />
          <FieldError message={errors.conducted_by?.message} />
        </div>

        <div className="space-y-2">
          <FieldLabel>Key Points Discussed</FieldLabel>
          <Controller
            control={control}
            name="key_points"
            render={({ field }) => (
              <Textarea
                {...field}
                value={field.value ?? ''}
                rows={3}
                maxLength={500}
                placeholder="Summary of key discussion points…"
              />
            )}
          />
          <FieldError message={errors.key_points?.message} />
        </div>

        {hasActivities ? (
          <div className="space-y-2">
            <FieldLabel>Associated Activity</FieldLabel>
            <Controller
              control={control}
              name="activity_entry_id"
              render={({ field }) => (
                <ChipGroup
                  options={[
                    { value: '', label: 'None' },
                    ...activities.map((a) => ({
                      value: a.id,
                      label:
                        a.description.length > 30
                          ? a.description.slice(0, 30) + '…'
                          : a.description,
                    })),
                  ]}
                  value={field.value ?? ''}
                  onChange={(v) => field.onChange(v)}
                  wrap
                />
              )}
            />
          </div>
        ) : (
          <div className="space-y-2">
            <FieldLabel>Associated Activity</FieldLabel>
            <Controller
              control={control}
              name="activity_description"
              render={({ field }) => (
                <Input
                  {...field}
                  value={field.value ?? ''}
                  maxLength={300}
                  placeholder="Describe the associated activity"
                />
              )}
            />
          </div>
        )}

        <div className="space-y-2">
          <FieldLabel>Comments</FieldLabel>
          <Controller
            control={control}
            name="comments"
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                maxLength={300}
                placeholder="Additional notes"
              />
            )}
          />
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
            onClick={onSubmit}
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : editing ? (
              'Update Meeting'
            ) : (
              'Save Meeting'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
